/**
 * @file `describeFilters` — the human "why they match" phrase for a
 *       recipient-filter set, shown on the compose surface + the review
 *       drawer before a send.
 * @module @labcraft/admin/ui/emails/describe-filters
 *
 * Pure (no JSX) so it lives in a `.ts` module the unit tests can import —
 * the compose panel's `.tsx` can't be loaded under Node's type-stripping.
 * Load-bearing for #1639: a tag-scoped send must make its tag scope visible
 * in the summary so the operator can confirm the audience is what they
 * intend before committing.
 */

import type { EmailRecipientFilters } from '../../shared/emails_routes.ts'

/**
 * A short human phrase describing a recipient-filter set (the "why they
 * match"). Composes the present filters with ` · ` separators; an empty set
 * reads "all learners". Tag include/exclude are rendered explicitly
 * (`tagged core, beta` / `not tagged churned`) so a tag-scoped audience is
 * never silently summarized as "all learners".
 *
 * @param f - The recipient-filter set.
 * @returns The summary phrase.
 */
export function describeFilters(f: EmailRecipientFilters): string {
  const parts: string[] = []
  if (f.status) parts.push(f.status)
  if (f.q) parts.push(`matching “${f.q}”`)
  if (f.signedUpAfter) {
    parts.push(`signed up after ${f.signedUpAfter.slice(0, 10)}`)
  }
  if (f.signedUpBefore) {
    parts.push(`signed up before ${f.signedUpBefore.slice(0, 10)}`)
  }
  if (f.acquisitionSource) parts.push(`via ${f.acquisitionSource}`)
  if (f.acquisitionCategory) parts.push(f.acquisitionCategory)
  if (f.hasProgress !== undefined) {
    parts.push(f.hasProgress ? 'started a course' : 'never started')
  }
  if (f.tags && f.tags.length > 0) parts.push(`tagged ${f.tags.join(', ')}`)
  if (f.tagsNot && f.tagsNot.length > 0) {
    parts.push(`not tagged ${f.tagsNot.join(', ')}`)
  }
  return parts.length > 0 ? parts.join(' · ') : 'all learners'
}
